/**
 * Subtle UI feedback sounds — hover, toggle on/off.
 * Respects settingSounds and settingUiVolume (same keys as Settings).
 */

const UI_VOLUME_DEFAULT = 6;
const HOVER_GAIN = 0.018;
const TOGGLE_GAIN = 0.028;

const audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
const masterGain = audioCtx.createGain();
masterGain.connect(audioCtx.destination);

function syncMasterGain() {
    const mobile =
        typeof matchMedia !== "undefined" &&
        (matchMedia("(pointer: coarse)").matches ||
            (matchMedia("(max-width: 820px)").matches && navigator.maxTouchPoints > 0));
    masterGain.gain.value = mobile ? 2.35 : 1;
}

syncMasterGain();
window.addEventListener("resize", syncMasterGain);

function readSoundsEnabled(): boolean {
    return localStorage.getItem("settingSounds") !== "0";
}

function readUiVolume(): number {
    const raw = localStorage.getItem("settingUiVolume");
    const n = raw != null && raw !== "" ? Number(raw) : UI_VOLUME_DEFAULT;
    const v = Number.isFinite(n) ? n : UI_VOLUME_DEFAULT;
    return Math.max(0, Math.min(10, v));
}

function uiPeak(base: number): number {
    if (!readSoundsEnabled() || readUiVolume() <= 0) return 0;
    return base * (readUiVolume() / 10);
}

function isAudioRunning(): boolean {
    return audioCtx.state === "running";
}

function resumeAudio(): Promise<void> {
    if (audioCtx.state === "running") return Promise.resolve();
    return audioCtx.resume().catch(() => {});
}

function playTone(freq: number, when: number, duration: number, peak: number, type: OscillatorType = "sine") {
    if (!peak || peak <= 0 || !isAudioRunning()) return;
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, when);
    gain.gain.setValueAtTime(0.0001, when);
    gain.gain.exponentialRampToValueAtTime(Math.max(0.0001, peak), when + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
    osc.connect(gain);
    gain.connect(masterGain);
    osc.start(when);
    osc.stop(when + duration + 0.02);
}

function runUiSound(fn: () => void) {
    if (!readSoundsEnabled() || readUiVolume() <= 0) return;
    const play = () => {
        if (!isAudioRunning()) return;
        fn();
    };
    if (isAudioRunning()) play();
    else void resumeAudio().then(play);
}

export function playUiButtonHover() {
    runUiSound(() => {
        const t0 = audioCtx.currentTime + 0.004;
        playTone(1280, t0, 0.028, uiPeak(HOVER_GAIN), "sine");
    });
}

/** Two ascending steps — bright major third (positive). */
const TOGGLE_ON_FREQS = [523.25, 659.25];
/** Two descending steps — lower triangle (negative). */
const TOGGLE_OFF_FREQS = [440, 349.23];

export function playUiToggleOn() {
    runUiSound(() => {
        const vol = uiPeak(TOGGLE_GAIN);
        const t0 = audioCtx.currentTime + 0.006;
        let t = t0;
        for (const freq of TOGGLE_ON_FREQS) {
            playTone(freq, t, 0.062, vol, "sine");
            t += 0.052;
        }
    });
}

export function playUiToggleOff() {
    runUiSound(() => {
        const vol = uiPeak(TOGGLE_GAIN);
        const t0 = audioCtx.currentTime + 0.006;
        let t = t0;
        for (const freq of TOGGLE_OFF_FREQS) {
            playTone(freq, t, 0.062, vol * 0.92, "triangle");
            t += 0.052;
        }
    });
}

let sliderTickLastMs = 0;

/** Soft tick while dragging a slider (rate-limited). */
export function playUiSliderTick() {
    const now = performance.now();
    if (now - sliderTickLastMs < 52) return;
    sliderTickLastMs = now;
    runUiSound(() => {
        playTone(987.77, audioCtx.currentTime + 0.004, 0.022, uiPeak(0.011), "sine");
    });
}

let modeCardHoverLastMs = 0;

/** Gentle shimmer when hovering a mode card. */
export function playUiModeCardHover() {
    const now = performance.now();
    if (now - modeCardHoverLastMs < 110) return;
    modeCardHoverLastMs = now;
    runUiSound(() => {
        const t0 = audioCtx.currentTime + 0.004;
        playTone(880, t0, 0.034, uiPeak(0.014), "sine");
        playTone(1174.66, t0 + 0.018, 0.028, uiPeak(0.009), "sine");
    });
}

/** Mode selected on the Modes screen. */
export function playUiModeSelect() {
    runUiSound(() => {
        const vol = uiPeak(0.032);
        const t0 = audioCtx.currentTime + 0.006;
        playTone(523.25, t0, 0.07, vol, "sine");
        playTone(783.99, t0 + 0.055, 0.09, vol * 0.88, "sine");
    });
}

/** Mode deselected (toggle off). */
export function playUiModeDeselect() {
    runUiSound(() => {
        const vol = uiPeak(0.026);
        const t0 = audioCtx.currentTime + 0.006;
        playTone(440, t0, 0.08, vol, "triangle");
        playTone(329.63, t0 + 0.06, 0.1, vol * 0.82, "triangle");
    });
}

/** Switching between selected modes in the panel. */
export function playUiModeSwitch() {
    runUiSound(() => {
        const vol = uiPeak(0.028);
        const t0 = audioCtx.currentTime + 0.004;
        playTone(659.25, t0, 0.045, vol, "sine");
        playTone(987.77, t0 + 0.04, 0.065, vol * 0.9, "sine");
    });
}

/** Confirming PLAY on the mode panel. */
export function playUiModePanelPlay() {
    runUiSound(() => {
        const vol = uiPeak(0.038);
        const t0 = audioCtx.currentTime + 0.008;
        playTone(392, t0, 0.09, vol, "square");
        playTone(523.25, t0 + 0.07, 0.11, vol * 0.92, "square");
        playTone(659.25, t0 + 0.14, 0.14, vol * 0.78, "square");
    });
}

/** Classic coin / cha-ching when opening Shop or buying in Shop. */
export function playShopCoinSound() {
    runUiSound(() => {
        const vol = uiPeak(0.044);
        const t0 = audioCtx.currentTime + 0.004;
        playTone(987.77, t0, 0.048, vol, "square");
        playTone(1318.51, t0 + 0.042, 0.062, vol * 0.88, "square");
        playTone(1567.98, t0 + 0.095, 0.11, vol * 0.62, "sine");
        playTone(1975.53, t0 + 0.14, 0.08, vol * 0.35, "triangle");
    });
}

/** Subtle chime when Battle Pass XP is granted. */
export function playBattlePassXpSound() {
    runUiSound(() => {
        const vol = uiPeak(0.02);
        const t0 = audioCtx.currentTime + 0.004;
        playTone(784, t0, 0.05, vol, "sine");
        playTone(988, t0 + 0.04, 0.06, vol * 0.75, "sine");
    });
}

/** Fanfare when the Battle Pass level increases. */
export function playBattlePassLevelUpSound() {
    runUiSound(() => {
        const vol = uiPeak(0.046);
        const t0 = audioCtx.currentTime + 0.006;
        playTone(523.25, t0, 0.09, vol, "triangle");
        playTone(659.25, t0 + 0.08, 0.1, vol * 0.9, "triangle");
        playTone(783.99, t0 + 0.16, 0.12, vol, "sine");
        playTone(1046.5, t0 + 0.26, 0.16, vol * 0.72, "sine");
    });
}

/** Unlock sting when a Battle Pass reward is claimed. */
export function playBattlePassClaimSound() {
    runUiSound(() => {
        const vol = uiPeak(0.04);
        const t0 = audioCtx.currentTime + 0.004;
        playTone(880, t0, 0.05, vol, "square");
        playTone(1174.66, t0 + 0.05, 0.08, vol * 0.82, "sine");
        playTone(1567.98, t0 + 0.11, 0.12, vol * 0.55, "triangle");
    });
}

/** Soft tick while a crate roll strip is spinning. */
export function playCrateRollTick(intensity = 1) {
    const t = Math.max(0.35, Math.min(1.2, intensity));
    runUiSound(() => {
        const vol = uiPeak(0.016 * t);
        const t0 = audioCtx.currentTime + 0.002;
        playTone(720 + 380 * t, t0, 0.028, vol, "square");
    });
}

/** Stinger when the crate roll lands on a cosmetic. */
export function playCrateRevealSound() {
    runUiSound(() => {
        const vol = uiPeak(0.05);
        const t0 = audioCtx.currentTime + 0.004;
        playTone(523.25, t0, 0.07, vol * 0.85, "triangle");
        playTone(783.99, t0 + 0.055, 0.09, vol, "square");
        playTone(1046.5, t0 + 0.12, 0.16, vol * 0.72, "sine");
        playTone(1567.98, t0 + 0.2, 0.12, vol * 0.4, "triangle");
    });
}

export type ShopHoldChargeHandle = {
    /** Stop the rising charge; `cancel` plays nothing here (caller plays cancel SFX). */
    stop: (mode: "complete" | "cancel") => void;
};

/**
 * Continuous rising tone for Shop hold-to-buy (pitch + intensity climb over `durationMs`).
 */
export function startShopHoldChargeSound(durationMs: number): ShopHoldChargeHandle {
    let osc: OscillatorNode | null = null;
    let gain: GainNode | null = null;
    let stopped = false;

    const stop = (_mode: "complete" | "cancel") => {
        if (stopped) return;
        stopped = true;
        const now = audioCtx.currentTime;
        try {
            if (gain) {
                gain.gain.cancelScheduledValues(now);
                gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), now);
                gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
            }
            osc?.stop(now + 0.06);
        } catch {
            /* already stopped */
        }
        osc = null;
        gain = null;
    };

    runUiSound(() => {
        if (stopped) return;
        const vol = uiPeak(0.032);
        if (!vol) return;
        const t0 = audioCtx.currentTime + 0.004;
        const dur = Math.max(0.2, durationMs / 1000);
        osc = audioCtx.createOscillator();
        gain = audioCtx.createGain();
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(196, t0);
        osc.frequency.exponentialRampToValueAtTime(740, t0 + dur);
        gain.gain.setValueAtTime(0.0001, t0);
        gain.gain.exponentialRampToValueAtTime(vol * 0.35, t0 + 0.05);
        gain.gain.linearRampToValueAtTime(vol, t0 + dur);
        // Soft lowpass so the saw isn’t harsh
        const filter = audioCtx.createBiquadFilter();
        filter.type = "lowpass";
        filter.frequency.setValueAtTime(900, t0);
        filter.frequency.exponentialRampToValueAtTime(2800, t0 + dur);
        osc.connect(filter);
        filter.connect(gain);
        gain.connect(masterGain);
        osc.start(t0);
        osc.stop(t0 + dur + 0.15);
    });

    return { stop };
}

/** Distinct confirm chime when Shop hold-to-buy completes (not coin / bounce). */
export function playShopHoldConfirmSound() {
    runUiSound(() => {
        const vol = uiPeak(0.048);
        const t0 = audioCtx.currentTime + 0.004;
        // Warm fifth + sparkle — different contour from playShopCoinSound
        playTone(392, t0, 0.09, vol * 0.7, "triangle");
        playTone(587.33, t0 + 0.05, 0.11, vol, "sine");
        playTone(784, t0 + 0.11, 0.14, vol * 0.85, "triangle");
        playTone(1174.66, t0 + 0.18, 0.16, vol * 0.45, "sine");
    });
}

/** Short descending cancel when Shop hold-to-buy is released early. */
export function playShopHoldCancelSound() {
    runUiSound(() => {
        const vol = uiPeak(0.03);
        const t0 = audioCtx.currentTime + 0.002;
        playTone(520, t0, 0.07, vol, "triangle");
        playTone(330, t0 + 0.045, 0.1, vol * 0.75, "sine");
        playTone(220, t0 + 0.09, 0.08, vol * 0.4, "triangle");
    });
}

const INTERACTIVE_HOVER_SELECTOR = [
    "button:not(:disabled)",
    ".btn:not(:disabled)",
    "a[href]",
    ".toggleSwitch:not(:disabled)",
    ".gameSelectTrigger:not(:disabled)",
    "select:not(:disabled)",
    ".sideNavBtn",
    ".modeBoxMenuBtn",
    ".modeBox button:not(:disabled)",
    '[role="button"]:not([aria-disabled="true"])',
    "input[type='button']:not(:disabled)",
    "input[type='submit']:not(:disabled)",
].join(",");

function isInteractiveHoverTarget(el: Element | null): el is HTMLElement {
    if (!el || !(el instanceof HTMLElement)) return false;
    if (el.disabled || el.getAttribute("aria-disabled") === "true") return false;
    if (el.closest("#game, #gameCursorTop")) return false;
    if (el.dataset.uiSound === "off") return false;
    return !!el.closest(INTERACTIVE_HOVER_SELECTOR);
}

let lastHoverEl: Element | null = null;
let uiSoundsMounted = false;
const toggleWasOnBeforeClick = new WeakMap<Element, boolean>();

export function mountUiSounds(): void {
    if (uiSoundsMounted) return;
    uiSoundsMounted = true;

    const primeAudio = () => {
        void resumeAudio();
    };
    document.addEventListener("pointerdown", primeAudio, { capture: true, passive: true, once: true });
    document.addEventListener("keydown", primeAudio, { capture: true, once: true });

    document.addEventListener(
        "pointerover",
        (e) => {
            if (!(e.target instanceof Element) || e.pointerType !== "mouse") return;
            const el = e.target.closest(INTERACTIVE_HOVER_SELECTOR);
            if (!isInteractiveHoverTarget(el)) return;
            const from = e.relatedTarget;
            if (from instanceof Element && el.contains(from)) return;
            if (el === lastHoverEl) return;
            lastHoverEl = el;
            playUiButtonHover();
        },
        true,
    );

    document.addEventListener(
        "pointerout",
        (e) => {
            if (!(e.target instanceof Element)) return;
            const el = e.target.closest(INTERACTIVE_HOVER_SELECTOR);
            if (!el || el !== lastHoverEl) return;
            const to = e.relatedTarget;
            if (to instanceof Element && el.contains(to)) return;
            lastHoverEl = null;
        },
        true,
    );

    document.addEventListener(
        "click",
        (e) => {
            const toggle = e.target instanceof Element ? e.target.closest(".toggleSwitch:not(:disabled)") : null;
            if (!toggle) return;
            toggleWasOnBeforeClick.set(toggle, toggle.getAttribute("aria-pressed") === "true");
        },
        true,
    );

    document.addEventListener(
        "click",
        (e) => {
            const toggle = e.target instanceof Element ? e.target.closest(".toggleSwitch:not(:disabled)") : null;
            if (!toggle) return;
            const wasOn = toggleWasOnBeforeClick.get(toggle);
            toggleWasOnBeforeClick.delete(toggle);
            if (wasOn === undefined) return;
            const nowOn = toggle.getAttribute("aria-pressed") === "true";
            if (nowOn === wasOn) return;
            if (nowOn) playUiToggleOn();
            else playUiToggleOff();
        },
        false,
    );
}
